import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
    hasNext: false,
    hasPrev: false
  });

  // Pagination constants
  const usersPerPage = 10;

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 480);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSmallScreen(window.innerWidth < 480);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

  // Fetch users from backend
  const fetchUsers = async (page = 1, search = '') => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      
      if (!token) {
        toast.error('Authentication required');
        setLoading(false);
        return;
      }

      // Calculate skip for pagination
      const skip = (page - 1) * usersPerPage;
      
      const queryParams = new URLSearchParams({
        limit: usersPerPage.toString(),
        skip: skip.toString(),
        ...(search && { search })
      });

      const response = await fetch(`${API_URL}/admin/users?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const usersList = data.data?.users || data.data || [];
        setUsers(usersList);
        
        // Calculate pagination
        const totalUsers = data.data?.total || data.total || usersList.length + (page - 1) * usersPerPage;
        const totalPages = Math.ceil(totalUsers / usersPerPage);
        
        setPagination({
          currentPage: page,
          totalPages,
          totalUsers,
          hasNext: page < totalPages,
          hasPrev: page > 1
        });
      } else {
        throw new Error(data.message || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(error.message || 'Failed to load users');
      setUsers([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalUsers: 0,
        hasNext: false,
        hasPrev: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchUsers(1, searchTerm);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      fetchUsers(page, searchTerm);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = isSmallScreen ? 3 : (isMobile ? 5 : 7);
    
    let startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust if we're at the beginning
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return pageNumbers;
  };

  // Update user status
  const updateUserStatus = async (userId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/admin/users/${userId}/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isActive: newStatus === 'active' })
      });

      const data = await response.json();

      if (data.success) {
        // Update the user in the local state
        setUsers(prev => prev.map(user =>
          user._id === userId ? { 
            ...user, 
            isActive: newStatus === 'active',
            status: newStatus === 'active' ? 'active' : 'inactive'
          } : user
        ));
        
        // If selected user is being updated, update that too
        if (selectedUser && selectedUser._id === userId) {
          setSelectedUser(prev => ({
            ...prev,
            isActive: newStatus === 'active',
            status: newStatus === 'active' ? 'active' : 'inactive'
          }));
        }
        
        toast.success(data.message || `User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        setSelectedUser(null);
      } else {
        throw new Error(data.message || 'Failed to update user status');
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error(error.message || 'Failed to update user status');
    }
  };

  // Format date - RESPONSIVE
  const formatDate = (dateString) => {
    try {
      if (isSmallScreen) {
        return new Date(dateString).toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric'
        });
      }
      if (isMobile) {
        return new Date(dateString).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
      }
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (isSmallScreen) {
      return `KSh ${amount?.toLocaleString() || '0'}`;
    }
    return `KSh ${amount?.toLocaleString() || '0'}`;
  };

  // Get full name
  const getFullName = (user) => {
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    if (isSmallScreen && fullName.length > 12) {
      return fullName.substring(0, 10) + '...';
    }
    return fullName;
  };

  // Get status for display (maps isActive boolean to status string)
  const getStatus = (user) => {
    return user.isActive ? 'active' : 'inactive';
  };

  // Get status display for mobile
  const getStatusDisplay = (status, isSmallScreen = false) => {
    return isSmallScreen ? status.substring(0, 3) : status;
  };

  return (
    <div className="admin-content" style={{ 
      padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)'),
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)'),
        paddingBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
        borderBottom: '1px solid var(--border-light)'
      }}>
        <h1 className="accent-gold" style={{ 
          fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-3xl)'),
          marginBottom: isSmallScreen ? 'var(--space-1)' : (isMobile ? 'var(--space-2)' : 'var(--space-3)')
        }}>
          Users <span style={{ color: 'var(--text-dark)' }}>Management</span>
        </h1>
        
        <div style={{ 
          display: 'flex', 
          flexDirection: isSmallScreen ? 'column' : (isMobile ? 'column' : 'row'),
          alignItems: isSmallScreen ? 'flex-start' : (isMobile ? 'flex-start' : 'center'),
          gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
          justifyContent: 'space-between'
        }}>
          <p style={{ 
            color: 'var(--text-medium)',
            fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
          }}>
            Manage customer accounts and user permissions
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{ 
        marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)') 
      }}>
        <h2 style={{ 
          fontSize: isSmallScreen ? 'var(--font-size-base)' : (isMobile ? 'var(--font-size-lg)' : 'var(--font-size-xl)'),
          marginBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
          color: 'var(--text-dark)'
        }}>
          User Statistics
        </h2>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: isSmallScreen ? 'repeat(2, 1fr)' : 
                               isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: isSmallScreen ? '8px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
        }}>
          <div className="stat-card gold" style={{
            padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
            minHeight: isSmallScreen ? '80px' : (isMobile ? '100px' : '120px'),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div className="stat-value" style={{ 
              color: 'var(--accent-gold)',
              fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
              lineHeight: 1.2,
              marginBottom: '4px'
            }}>
              {pagination.totalUsers}
            </div>
            <div style={{ 
              color: 'var(--text-light)', 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
              fontWeight: '500'
            }}>
              Total Users
            </div>
          </div>
          
          <div className="stat-card" style={{
            padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
            minHeight: isSmallScreen ? '80px' : (isMobile ? '100px' : '120px'),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div className="stat-value" style={{ 
              color: 'var(--success)',
              fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
              lineHeight: 1.2,
              marginBottom: '4px'
            }}>
              {users.filter(u => u.isActive).length}
            </div>
            <div style={{ 
              color: 'var(--text-light)', 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
              fontWeight: '500'
            }}>
              Active Users
            </div>
          </div>
          
          <div className="stat-card" style={{
            padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
            minHeight: isSmallScreen ? '80px' : (isMobile ? '100px' : '120px'),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div className="stat-value" style={{ 
              color: 'var(--warning)',
              fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
              lineHeight: 1.2,
              marginBottom: '4px'
            }}>
              {users.filter(u => !u.isActive).length}
            </div>
            <div style={{ 
              color: 'var(--text-light)', 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
              fontWeight: '500'
            }}>
              Inactive Users
            </div>
          </div>
          
          <div className="stat-card" style={{
            padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
            minHeight: isSmallScreen ? '80px' : (isMobile ? '100px' : '120px'),
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between'
          }}>
            <div className="stat-value" style={{ 
              color: 'var(--info)',
              fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
              lineHeight: 1.2,
              marginBottom: '4px'
            }}>
              {users.reduce((total, user) => total + (user.ordersCount || 0), 0)}
            </div>
            <div style={{ 
              color: 'var(--text-light)', 
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
              fontWeight: '500'
            }}>
              Total Orders
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card card-gold" style={{ marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)') }}>
        <div className="card-header" style={{
          padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: isSmallScreen ? 'wrap' : (isMobile ? 'wrap' : 'nowrap'),
          gap: isSmallScreen ? 'var(--space-1)' : (isMobile ? 'var(--space-2)' : '0')
        }}>
          <h3 style={{ 
            margin: 0,
            fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            👥 All Users ({pagination.totalUsers})
          </h3>
          
          {/* Search Box */}
          <div style={{ position: 'relative', width: isSmallScreen ? '100%' : (isMobile ? '100%' : '300px') }}>
            <input
              type="text"
              placeholder={isSmallScreen ? "Search users..." : "Search users by name, email, or phone..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input"
              style={{ 
                width: '100%',
                paddingLeft: '35px',
                padding: isSmallScreen ? '6px 10px 6px 35px' : (isMobile ? '8px 12px 8px 35px' : '10px 16px 10px 35px'),
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
              }}
            />
            <span style={{
              position: 'absolute',
              left: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--text-light)',
              fontSize: isSmallScreen ? '0.9rem' : '1rem'
            }}>
              🔍
            </span>
          </div>
        </div>
        
        <div className="card-body" style={{
          padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
        }}>
          {loading ? (
            <div style={{ 
              textAlign: 'center', 
              padding: isSmallScreen ? 'var(--space-4)' : 'var(--space-8)', 
              color: 'var(--text-light)'
            }}>
              Loading users...
            </div>
          ) : users.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: isSmallScreen ? 'var(--space-4)' : 'var(--space-8)', 
              color: 'var(--text-light)'
            }}>
              <p style={{ fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-base)' }}>
                {searchTerm ? 'No users found matching your search.' : 'No users found.'}
              </p>
            </div>
          ) : isSmallScreen ? (
            // Extra small screen view (card layout)
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {users.map((user) => (
                <div key={user._id} style={{
                  padding: '12px',
                  backgroundColor: 'var(--bg-light)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-light)',
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr',
                  gap: '12px',
                  alignItems: 'start'
                }}>
                  {/* User Avatar & Status */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <div style={{
                      width: '35px',
                      height: '35px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--accent-gold)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--text-dark)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-xs)'
                    }}>
                      {getFullName(user).charAt(0).toUpperCase()}
                    </div>
                    <div style={{ 
                      backgroundColor: getStatus(user) === 'active' ? 'var(--success)' : 'var(--error)',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '8px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      textAlign: 'center',
                      minWidth: '35px'
                    }}>
                      {getStatusDisplay(getStatus(user), true)}
                    </div>
                  </div>
                  
                  {/* User Details */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <div style={{ 
                      fontWeight: '700', 
                      color: 'var(--text-dark)',
                      fontSize: 'var(--font-size-xs)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {getFullName(user)}
                    </div>
                    
                    <div style={{ 
                      fontSize: '9px', 
                      color: 'var(--text-light)',
                      marginBottom: '2px'
                    }}>
                      {user.email}
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      fontSize: '9px'
                    }}>
                      <span style={{ color: 'var(--text-medium)' }}>Orders:</span>
                      <span style={{ fontWeight: '500' }}>
                        {user.ordersCount || 0}
                      </span>
                    </div>
                    
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      fontSize: '9px'
                    }}>
                      <span style={{ color: 'var(--text-medium)' }}>Spent:</span>
                      <span style={{ fontWeight: '700', color: 'var(--success)' }}>
                        {formatCurrency(user.totalSpent || 0)}
                      </span>
                    </div>
                    
                    <div style={{ 
                      fontSize: '8px', 
                      color: 'var(--text-light)',
                      marginTop: '2px'
                    }}>
                      Joined: {user.createdAt ? formatDate(user.createdAt) : 'N/A'}
                    </div>
                    
                    {/* Actions */}
                    <div style={{ 
                      display: 'flex', 
                      gap: '4px',
                      marginTop: '4px'
                    }}>
                      <button 
                        className="btn btn-secondary"
                        style={{ 
                          padding: '4px 8px',
                          fontSize: '9px',
                          flex: 1
                        }}
                        onClick={() => setSelectedUser(user)}
                      >
                        View
                      </button>
                      
                      {getStatus(user) === 'active' ? (
                        <button 
                          className="btn btn-warning"
                          style={{ 
                            padding: '4px 8px',
                            fontSize: '9px',
                            flex: 1
                          }}
                          onClick={() => updateUserStatus(user._id, 'inactive')}
                        >
                          {isSmallScreen ? 'Deact' : 'Deactivate'}
                        </button>
                      ) : (
                        <button 
                          className="btn btn-success"
                          style={{ 
                            padding: '4px 8px',
                            fontSize: '9px',
                            flex: 1
                          }}
                          onClick={() => updateUserStatus(user._id, 'active')}
                        >
                          {isSmallScreen ? 'Activ' : 'Activate'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : isMobile ? (
            // Mobile view: Card layout
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {users.map((user) => (
                <div key={user._id} style={{
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--bg-light)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-light)'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    marginBottom: 'var(--space-2)'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--accent-gold)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--text-dark)',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)'
                    }}>
                      {getFullName(user).charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: '600', 
                        color: 'var(--text-dark)',
                        fontSize: 'var(--font-size-sm)'
                      }}>
                        {getFullName(user)}
                      </div>
                      <div style={{ 
                        fontSize: 'var(--font-size-xs)', 
                        color: 'var(--text-light)'
                      }}>
                        {user.email}
                      </div>
                    </div>
                    <span 
                      style={{ 
                        backgroundColor: getStatus(user) === 'active' ? 'var(--success)' : 'var(--error)',
                        color: 'white',
                        padding: '4px 10px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: '600',
                        textTransform: 'capitalize'
                      }}
                    >
                      {getStatusDisplay(getStatus(user), false)}
                    </span>
                  </div>
                  
                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 'var(--space-2)',
                    fontSize: 'var(--font-size-sm)',
                    marginBottom: 'var(--space-2)'
                  }}>
                    <div>
                      <div style={{ color: 'var(--text-medium)' }}>Orders</div>
                      <div style={{ fontWeight: '600', color: 'var(--text-dark)' }}>
                        {user.ordersCount || 0}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-medium)' }}>Total Spent</div>
                      <div style={{ fontWeight: '600', color: 'var(--success)' }}>
                        {formatCurrency(user.totalSpent || 0)}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-medium)' }}>Role</div>
                      <div style={{ 
                        fontWeight: '500', 
                        color: 'var(--text-dark)',
                        textTransform: 'capitalize'
                      }}>
                        {user.role || 'customer'}
                      </div>
                    </div>
                    <div>
                      <div style={{ color: 'var(--text-medium)' }}>Joined</div>
                      <div style={{ 
                        fontSize: 'var(--font-size-xs)', 
                        color: 'var(--text-dark)'
                      }}>
                        {user.createdAt ? formatDate(user.createdAt) : 'N/A'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div style={{ 
                    display: 'flex', 
                    gap: 'var(--space-2)',
                    marginTop: 'var(--space-2)'
                  }}>
                    <button 
                      className="btn btn-secondary"
                      style={{ 
                        padding: 'var(--space-2) var(--space-3)',
                        fontSize: 'var(--font-size-xs)',
                        flex: 1
                      }}
                      onClick={() => setSelectedUser(user)}
                    >
                      View Details
                    </button>
                    
                    {getStatus(user) === 'active' ? (
                      <button 
                        className="btn btn-warning"
                        style={{ 
                          padding: 'var(--space-2) var(--space-3)',
                          fontSize: 'var(--font-size-xs)',
                          flex: 1
                        }}
                        onClick={() => updateUserStatus(user._id, 'inactive')}
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button 
                        className="btn btn-success"
                        style={{ 
                          padding: 'var(--space-2) var(--space-3)',
                          fontSize: 'var(--font-size-xs)',
                          flex: 1
                        }}
                        onClick={() => updateUserStatus(user._id, 'active')}
                      >
                        Activate
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Desktop view: Table layout with horizontal scroll
            <div style={{ 
              overflowX: 'auto',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-light)'
            }}>
              <table style={{ 
                width: '100%', 
                borderCollapse: 'collapse',
                minWidth: '800px'
              }}>
                <thead>
                  <tr style={{ 
                    borderBottom: '2px solid var(--border-light)',
                    backgroundColor: 'var(--bg-light)'
                  }}>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>User</th>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Contact</th>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Orders</th>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Total Spent</th>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Status</th>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Joined</th>
                    <th style={{ 
                      padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'), 
                      textAlign: 'left', 
                      color: 'var(--text-medium)',
                      fontWeight: '600',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      whiteSpace: 'nowrap'
                    }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user._id} style={{ 
                      borderBottom: '1px solid var(--border-light)',
                      transition: 'background-color 0.2s ease'
                    }}>
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)')
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: isSmallScreen ? '8px' : 'var(--space-3)' }}>
                          <div style={{
                            width: isSmallScreen ? '30px' : (isMobile ? '35px' : '40px'),
                            height: isSmallScreen ? '30px' : (isMobile ? '35px' : '40px'),
                            borderRadius: '50%',
                            backgroundColor: 'var(--accent-gold)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--text-dark)',
                            fontWeight: '600',
                            fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                          }}>
                            {getFullName(user).charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div style={{ 
                              fontWeight: '600', 
                              color: 'var(--text-dark)',
                              fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                            }}>
                              {getFullName(user)}
                            </div>
                            <div style={{ 
                              fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                              color: 'var(--text-light)',
                              textTransform: 'capitalize'
                            }}>
                              {user.role || 'customer'}
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)')
                      }}>
                        <div style={{ 
                          fontWeight: '500', 
                          color: 'var(--text-dark)',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}>
                          {user.email}
                        </div>
                        {user.phone && (
                          <div style={{ 
                            fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)', 
                            color: 'var(--text-light)'
                          }}>
                            📞 {user.phone}
                          </div>
                        )}
                      </td>
                      
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'),
                        textAlign: 'center'
                      }}>
                        <div style={{ 
                          fontWeight: '600', 
                          color: 'var(--text-dark)',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}>
                          {user.ordersCount || 0}
                        </div>
                      </td>
                      
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'),
                        textAlign: 'center'
                      }}>
                        <div style={{ 
                          fontWeight: '600', 
                          color: 'var(--success)',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                        }}>
                          {formatCurrency(user.totalSpent || 0)}
                        </div>
                      </td>
                      
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)')
                      }}>
                        <span 
                          style={{ 
                            backgroundColor: getStatus(user) === 'active' ? 'var(--success)' : 'var(--error)',
                            color: 'white',
                            padding: '4px 10px',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                            fontWeight: '600',
                            textTransform: 'capitalize',
                            display: 'inline-block',
                            minWidth: isSmallScreen ? '50px' : '70px',
                            textAlign: 'center'
                          }}
                        >
                          {getStatusDisplay(getStatus(user), isSmallScreen)}
                        </span>
                      </td>
                      
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'),
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                        color: 'var(--text-dark)'
                      }}>
                        {user.createdAt ? formatDate(user.createdAt) : 'N/A'}
                      </td>
                      
                      <td style={{ 
                        padding: isSmallScreen ? '8px 12px' : (isMobile ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)')
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          gap: isSmallScreen ? '4px' : 'var(--space-2)',
                          flexWrap: 'wrap'
                        }}>
                          <button 
                            className="btn btn-secondary"
                            style={{ 
                              padding: isSmallScreen ? '4px 8px' : 'var(--space-2) var(--space-3)',
                              fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)'
                            }}
                            onClick={() => setSelectedUser(user)}
                          >
                            View
                          </button>
                          
                          {getStatus(user) === 'active' ? (
                            <button 
                              className="btn btn-warning"
                              style={{ 
                                padding: isSmallScreen ? '4px 8px' : 'var(--space-2) var(--space-3)',
                                fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)'
                              }}
                              onClick={() => updateUserStatus(user._id, 'inactive')}
                            >
                              {isSmallScreen ? 'Deact' : 'Deactivate'}
                            </button>
                          ) : (
                            <button 
                              className="btn btn-success"
                              style={{ 
                                padding: isSmallScreen ? '4px 8px' : 'var(--space-2) var(--space-3)',
                                fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)'
                              }}
                              onClick={() => updateUserStatus(user._id, 'active')}
                            >
                              {isSmallScreen ? 'Activ' : 'Activate'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Component */}
          {pagination.totalPages > 1 && (
            <div style={{
              marginTop: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)'),
              paddingTop: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)',
              borderTop: '1px solid var(--border-light)',
              display: 'flex',
              flexDirection: isSmallScreen ? 'column' : 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: isSmallScreen ? 'var(--space-2)' : '0'
            }}>
              {/* Page Info */}
              <div style={{
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                color: 'var(--text-medium)',
                marginBottom: isSmallScreen ? 'var(--space-1)' : '0'
              }}>
                Showing {(pagination.currentPage - 1) * usersPerPage + 1} to {Math.min(pagination.currentPage * usersPerPage, pagination.totalUsers)} of {pagination.totalUsers} users
              </div>

              {/* Pagination Controls */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: isSmallScreen ? '4px' : 'var(--space-2)'
              }}>
                {/* First Page Button */}
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.currentPage === 1}
                  className="btn btn-secondary"
                  style={{
                    padding: isSmallScreen ? '6px 8px' : '8px 12px',
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    opacity: pagination.currentPage === 1 ? 0.5 : 1,
                    cursor: pagination.currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSmallScreen ? '««' : 'First'}
                </button>

                {/* Previous Page Button */}
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="btn btn-secondary"
                  style={{
                    padding: isSmallScreen ? '6px 8px' : '8px 12px',
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    opacity: pagination.currentPage === 1 ? 0.5 : 1,
                    cursor: pagination.currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSmallScreen ? '«' : 'Previous'}
                </button>

                {/* Page Numbers */}
                <div style={{
                  display: 'flex',
                  gap: isSmallScreen ? '2px' : '4px'
                }}>
                  {getPageNumbers().map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={pageNum === pagination.currentPage ? 'btn btn-gold' : 'btn btn-secondary'}
                      style={{
                        padding: isSmallScreen ? '6px 8px' : '8px 12px',
                        fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                        minWidth: isSmallScreen ? '32px' : '40px',
                        fontWeight: pageNum === pagination.currentPage ? '600' : '400'
                      }}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>

                {/* Next Page Button */}
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="btn btn-secondary"
                  style={{
                    padding: isSmallScreen ? '6px 8px' : '8px 12px',
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    opacity: pagination.currentPage === pagination.totalPages ? 0.5 : 1,
                    cursor: pagination.currentPage === pagination.totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSmallScreen ? '»' : 'Next'}
                </button>

                {/* Last Page Button */}
                <button
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="btn btn-secondary"
                  style={{
                    padding: isSmallScreen ? '6px 8px' : '8px 12px',
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                    opacity: pagination.currentPage === pagination.totalPages ? 0.5 : 1,
                    cursor: pagination.currentPage === pagination.totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  {isSmallScreen ? '»»' : 'Last'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* User Details Modal - RESPONSIVE */}
      {selectedUser && (
        <UserDetailsModal 
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onStatusUpdate={updateUserStatus}
          getFullName={getFullName}
          getStatus={getStatus}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          isMobile={isMobile}
          isSmallScreen={isSmallScreen}
        />
      )}
    </div>
  );
};

// Responsive User Details Modal Component
const UserDetailsModal = ({ 
  user, 
  onClose, 
  onStatusUpdate, 
  getFullName, 
  getStatus, 
  formatDate, 
  formatCurrency,
  isMobile,
  isSmallScreen 
}) => {
  // Responsive modal styles
  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
  };

  const contentStyle = {
    width: '100%',
    maxWidth: isSmallScreen ? '95vw' : (isMobile ? '90vw' : '600px'),
    maxHeight: isSmallScreen ? '85vh' : '90vh',
    overflowY: 'auto',
    borderRadius: isSmallScreen ? 'var(--radius-md)' : 'var(--radius-lg)'
  };

  return (
    <div style={modalStyle}>
      <div className="card" style={contentStyle}>
        <div className="card-header" style={{
          padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)'),
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h3 style={{ 
            margin: 0,
            fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)'
          }}>
            <span style={{ color: 'var(--accent-gold)' }}>👤</span>
            {isSmallScreen ? 'User Details' : `User: ${getFullName(user)}`}
          </h3>
          <button 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: isSmallScreen ? '1.3rem' : '1.5rem',
              cursor: 'pointer',
              color: 'var(--text-light)',
              padding: isSmallScreen ? '4px' : '8px',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        <div className="card-body" style={{
          padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
        }}>
          <div style={{ display: 'grid', gap: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)') }}>
            
            {/* User Profile */}
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: isSmallScreen ? '60px' : (isMobile ? '70px' : '80px'),
                height: isSmallScreen ? '60px' : (isMobile ? '70px' : '80px'),
                borderRadius: '50%',
                backgroundColor: 'var(--accent-gold)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--text-dark)',
                fontWeight: '600',
                fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)'),
                margin: '0 auto var(--space-4)'
              }}>
                {getFullName(user).charAt(0).toUpperCase()}
              </div>
              <h4 style={{ 
                marginBottom: 'var(--space-2)', 
                color: 'var(--text-dark)',
                fontSize: isSmallScreen ? 'var(--font-size-base)' : (isMobile ? 'var(--font-size-lg)' : 'var(--font-size-xl)')
              }}>
                {getFullName(user)}
              </h4>
              <p style={{ 
                color: 'var(--text-light)', 
                margin: 0,
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
              }}>
                {user.role} • {getStatus(user) === 'active' ? 'Active' : 'Inactive'} User
              </p>
            </div>

            {/* User Information */}
            <div>
              <h4 style={{ 
                marginBottom: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)',
                color: 'var(--text-dark)',
                borderBottom: '2px solid var(--accent-gold)',
                paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
              }}>
                Contact Information
              </h4>
              <div style={{ 
                display: 'grid', 
                gap: isSmallScreen ? '8px' : 'var(--space-3)',
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
              }}>
                <div>
                  <strong>Email:</strong> {user.email}
                </div>
                <div>
                  <strong>Phone:</strong> {user.phone || 'Not provided'}
                </div>
                <div>
                  <strong>Member Since:</strong> {formatDate(user.createdAt)}
                </div>
                {user.lastLogin && (
                  <div>
                    <strong>Last Login:</strong> {formatDate(user.lastLogin)}
                  </div>
                )}
              </div>
            </div>

            {/* User Statistics */}
            <div>
              <h4 style={{ 
                marginBottom: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)',
                color: 'var(--text-dark)',
                borderBottom: '2px solid var(--accent-gold)',
                paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
              }}>
                Shopping Statistics
              </h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isSmallScreen ? '1fr 1fr' : '1fr 1fr', 
                gap: isSmallScreen ? '8px' : 'var(--space-4)',
                textAlign: 'center'
              }}>
                <div className="stat-card" style={{
                  padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
                }}>
                  <div className="stat-value" style={{ 
                    color: 'var(--accent-gold)',
                    fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)')
                  }}>
                    {user.ordersCount || 0}
                  </div>
                  <div style={{ 
                    color: 'var(--text-light)', 
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                  }}>
                    Total Orders
                  </div>
                </div>
                <div className="stat-card" style={{
                  padding: isSmallScreen ? '10px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)')
                }}>
                  <div className="stat-value" style={{ 
                    color: 'var(--success)',
                    fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-2xl)')
                  }}>
                    {formatCurrency(user.totalSpent)}
                  </div>
                  <div style={{ 
                    color: 'var(--text-light)', 
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                  }}>
                    Total Spent
                  </div>
                </div>
              </div>
            </div>

            {/* Account Management */}
            <div>
              <h4 style={{ 
                marginBottom: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)',
                color: 'var(--text-dark)',
                borderBottom: '2px solid var(--accent-gold)',
                paddingBottom: isSmallScreen ? 'var(--space-1)' : 'var(--space-2)',
                fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)')
              }}>
                Account Management
              </h4>
              <div style={{ textAlign: 'center' }}>
                {getStatus(user) === 'active' ? (
                  <button 
                    className="btn btn-warning"
                    onClick={() => onStatusUpdate(user._id, 'inactive')}
                    style={{
                      padding: isSmallScreen ? '8px 16px' : '10px 20px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                    }}
                  >
                    Deactivate Account
                  </button>
                ) : (
                  <button 
                    className="btn btn-success"
                    onClick={() => onStatusUpdate(user._id, 'active')}
                    style={{
                      padding: isSmallScreen ? '8px 16px' : '10px 20px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                    }}
                  >
                    Activate Account
                  </button>
                )}
                <p style={{ 
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)', 
                  color: 'var(--text-light)',
                  marginTop: 'var(--space-2)'
                }}>
                  {getStatus(user) === 'active' 
                    ? 'Deactivating will prevent this user from placing new orders.'
                    : 'Activating will allow this user to place orders again.'
                  }
                </p>
              </div>
            </div>

          </div>
        </div>

        <div className="card-footer" style={{ 
          padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)'),
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: isSmallScreen ? '8px' : 'var(--space-3)',
          borderTop: '1px solid var(--border-light)'
        }}>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={onClose}
            style={{
              padding: isSmallScreen ? '8px 12px' : '10px 20px',
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UsersManagement;